import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import '../../styles/pages/ProductDetail.css';

const ProductDetail = () => {
  const { productId } = useParams();
  const { getProducts, getCategories, loading, error } = useData();
  const products = getProducts();
  const categories = getCategories();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const product = products.find(p => p.id === productId);

  // Navigation functions for image gallery
  const totalImages = product?.images?.length || 0;

  const goToPrevious = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
  };

  const goToNext = () => {
    setSelectedImageIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
  };

  // Helper to get category names
  const getCategoryNames = (ids) => {
    if (!ids || !Array.isArray(ids)) return '';
    return ids.map(id => {
      const cat = categories.find(c => c.id === id);
      return cat ? cat.name : id;
    }).join(', ');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading product...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading product: {error}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <main className="product-detail-page mandala-bg">
        <div className="container">
          <div className="not-found">
            <h1>Product Not Found</h1>
            <p>The product you're looking for doesn't exist.</p>
            <Link to="/products" className="back-btn">← Back to Products</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="product-detail-page mandala-bg">
      <section className="product-detail-section">
        <div className="container">
          <Link to="/products" className="back-link">← Back to Products</Link>

          <div className="product-detail-grid">
            {/* Product Images */}
            <div className="product-gallery">
              <div className="main-image">
                <img
                  src={product.images?.[selectedImageIndex] || product.images?.[0] || 'https://via.placeholder.com/500x400?text=Product'}
                  alt={product.name}
                />
                {product.isFeatured && (
                  <span className="featured-badge">Featured</span>
                )}
                {/* Navigation Arrows */}
                {totalImages > 1 && (
                  <>
                    <button className="gallery-nav gallery-nav-prev" onClick={goToPrevious} aria-label="Previous image">
                      ‹
                    </button>
                    <button className="gallery-nav gallery-nav-next" onClick={goToNext} aria-label="Next image">
                      ›
                    </button>
                    <div className="image-counter">
                      {selectedImageIndex + 1} / {totalImages}
                    </div>
                  </>
                )}
              </div>
              {product.images?.length > 1 && (
                <div className="thumbnail-row">
                  {product.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`${product.name} ${idx + 1}`}
                      className={`thumbnail ${selectedImageIndex === idx ? 'active' : ''}`}
                      onClick={() => setSelectedImageIndex(idx)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="product-details">
              <span className="product-category">{getCategoryNames(product.categoryIds)}</span>
              <h1 className="product-title">{product.name}</h1>
              <p className="product-price">{product.price}</p>

              {/* CTA Button */}
              <div className="product-actions" style={{ marginBottom: '1.5rem' }}>
                {product.ctaLink && product.ctaLink.startsWith('http') ? (
                  <a
                    href={product.ctaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="enquiry-btn"
                  >
                    {product.ctaText || 'Enquire Now'}
                  </a>
                ) : (
                  <Link to={product.ctaLink || '/contact'} className="enquiry-btn">
                    {product.ctaText || 'Enquire Now'}
                  </Link>
                )}
              </div>

              <div className="product-description">
                {(product.fullDescription || product.shortDescription || '').split('\n').map((paragraph, idx) => (
                  paragraph.trim() && <p key={idx}>{paragraph}</p>
                ))}
              </div>

              {/* Features */}
              {product.features?.length > 0 && (
                <div className="product-features">
                  <h3>Features</h3>
                  <ul>
                    {product.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Specifications */}
              {product.specifications && product.specifications.length > 0 && (
                <div className="product-specs">
                  <h3>Specifications</h3>
                  <table>
                    <tbody>
                      {product.specifications.map((spec, idx) => (
                        <tr key={idx}>
                          <td>{spec.label}</td>
                          <td>{spec.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* CTA Button */}

            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
